import { pointedArch } from "./geometry";
import { round } from "./rng";

// Sandstone palette for the carved gateway (art/Doors.js).
export const TONES = {
  day: {
    stone: "url(#rm-stone)",
    dome: "url(#rm-dome)",
    opening: "url(#rm-opening)",
    line: "#8a5f33",
    shade: "url(#rm-stone-shade)",
    lit: false,
  },
};

/** Ribbed, slightly bulbous Rajput dome with an inverted-lotus finial. */
export const Dome = ({ cx, by, r, t, ribs = 0 }) => {
  const top = by - r * 1.22;
  const body = `M${round(cx - r)} ${by} C${round(cx - r * 1.1)} ${round(by - r * 0.75)} ${round(cx - r * 0.5)} ${round(by - r * 1.18)} ${cx} ${round(top)} C${round(cx + r * 0.5)} ${round(by - r * 1.18)} ${round(cx + r * 1.1)} ${round(by - r * 0.75)} ${round(cx + r)} ${by} Z`;
  const shade = `M${round(cx + r * 0.12)} ${round(top + r * 0.04)} C${round(cx + r * 0.62)} ${round(by - r * 1.1)} ${round(cx + r * 1.1)} ${round(by - r * 0.7)} ${round(cx + r)} ${by} L${round(cx + r * 0.3)} ${by} C${round(cx + r * 0.42)} ${round(by - r * 0.6)} ${round(cx + r * 0.32)} ${round(by - r)} ${round(cx + r * 0.12)} ${round(top + r * 0.04)} Z`;
  return (
    <g>
      <rect x={round(cx - r * 1.06)} y={by} width={round(r * 2.12)} height={round(r * 0.2)} fill={t.stone} />
      <path d={body} fill={t.dome} />
      <path d={shade} fill="#5a3010" opacity="0.18" />
      {Array.from({ length: Math.max(0, ribs - 1) }, (_, k) => {
        const bx = cx - r + (2 * r * (k + 1)) / ribs;
        return (
          <path
            key={k}
            d={`M${round(bx)} ${by} Q${round(cx + (bx - cx) * 1.12)} ${round(by - r * 0.85)} ${cx} ${round(top)}`}
            fill="none"
            stroke={t.line}
            strokeWidth={round(Math.max(0.6, r * 0.025))}
            opacity="0.35"
          />
        );
      })}
      <path
        d={`M${round(cx - r * 0.22)} ${round(top + r * 0.06)} Q${cx} ${round(top - r * 0.2)} ${round(cx + r * 0.22)} ${round(top + r * 0.06)} Z`}
        fill={t.dome}
      />
      <line x1={cx} y1={round(top - r * 0.1)} x2={cx} y2={round(top - r * 0.62)} stroke={t.line} strokeWidth={round(Math.max(0.8, r * 0.05))} />
      <circle cx={cx} cy={round(top - r * 0.24)} r={round(r * 0.09)} fill={t.dome} />
      <circle cx={cx} cy={round(top - r * 0.42)} r={round(r * 0.06)} fill={t.dome} />
    </g>
  );
};

/** Chhatri: domed pavilion on pillars with cusped openings and a deep chajja. */
export const Chhatri = ({ cx, by, w, t, ribs = 6, tall = false }) => {
  const plinth = w * 0.1;
  const pillarH = w * (tall ? 0.95 : 0.55);
  const top = by - plinth - pillarH;
  const body = w * 0.94;
  const pillarW = w * 0.075;
  const gap = (body - 4 * pillarW) / 3;
  const eave = top - w * 0.05;
  const drum = w * 0.11;
  return (
    <g>
      <rect x={round(cx - w / 2)} y={round(by - plinth)} width={w} height={round(plinth)} fill={t.stone} />
      <line x1={round(cx - w / 2)} y1={round(by - plinth + 2)} x2={round(cx + w / 2)} y2={round(by - plinth + 2)} stroke={t.line} strokeWidth="0.8" opacity="0.5" />
      <rect x={round(cx - body / 2)} y={round(top)} width={round(body)} height={round(pillarH)} fill={t.stone} />
      {Array.from({ length: 3 }, (_, i) => {
        const ox = cx - body / 2 + pillarW + i * (gap + pillarW);
        return (
          <path
            key={i}
            d={pointedArch({ x: ox, y: top + pillarH * 0.1, w: gap, h: pillarH * 0.9, spring: pillarH * 0.32 })}
            fill={TONES.day.opening}
            opacity={t.lit ? 0.6 : 0.78}
          />
        );
      })}
      {tall &&
        Array.from({ length: 4 }, (_, i) => {
          const px = cx - body / 2 + i * (gap + pillarW) + pillarW / 2;
          return (
            <g key={i} stroke={t.line} strokeWidth="0.8" opacity="0.45">
              <line x1={round(px)} y1={round(top + pillarH * 0.12)} x2={round(px)} y2={round(by - plinth)} />
              <line x1={round(px - pillarW / 2)} y1={round(by - plinth - pillarH * 0.08)} x2={round(px + pillarW / 2)} y2={round(by - plinth - pillarH * 0.08)} />
            </g>
          );
        })}
      <rect x={round(cx + body * 0.22)} y={round(top)} width={round(body * 0.28)} height={round(pillarH)} fill={t.shade} />
      <path
        d={`M${round(cx - w * 0.46)} ${round(eave)} L${round(cx + w * 0.46)} ${round(eave)} L${round(cx + w * 0.62)} ${round(top + w * 0.03)} L${round(cx - w * 0.62)} ${round(top + w * 0.03)} Z`}
        fill={t.stone}
      />
      <line x1={round(cx - w * 0.62)} y1={round(top + w * 0.03)} x2={round(cx + w * 0.62)} y2={round(top + w * 0.03)} stroke={t.line} strokeWidth="1" opacity="0.6" />
      {tall &&
        Array.from({ length: 7 }, (_, i) => {
          const bx = cx - w * 0.42 + (i * w * 0.84) / 6;
          return <path key={i} d={`M${round(bx - 3)} ${round(top + w * 0.03)} L${round(bx + 3)} ${round(top + w * 0.03)} L${round(bx)} ${round(top + w * 0.1)} Z`} fill={t.stone} opacity="0.9" />;
        })}
      <rect x={round(cx - w * 0.38)} y={round(eave - drum)} width={round(w * 0.76)} height={round(drum)} fill={t.stone} />
      {Array.from({ length: 9 }, (_, i) => (
        <circle key={i} cx={round(cx - w * 0.32 + (i * w * 0.64) / 8)} cy={round(eave - drum / 2)} r={round(w * 0.012 + 0.6)} fill={t.line} opacity="0.45" />
      ))}
      <Dome cx={cx} by={round(eave - drum)} r={round(w * 0.36)} t={t} ribs={ribs} />
    </g>
  );
};
